/**
 * Blockhyresavtal för företagsbostäder — Version 2026, as blocks.
 *
 * Ported from the approved Word template
 * `base-templates/MALL_Blockhyra_företagsbostadsuthyrning.docx` (legal
 * counsel) via the legacy React template. Clause bodies are verbatim — do
 * not paraphrase or reorder. Sections are numbered automatically (§ 1–N):
 * the single-checkbox provisions self-hide and the rest renumber, exactly
 * as the legacy `metadata.sections[].visibleWhen` did.
 *
 * Every printed party datum is a slot value (bound to its party via
 * `autofill`), never read from the compile context.
 */
import { attachmentList, br, em, h, nodes, option, optionList, p, section, t, table, val } from '@hyranai/regta-document-ir';
import { annexOrRule, contextAttachments } from '../common/attachments.js';
import { fieldRows, partyDetailsBlock, referenceLine } from '../common/fields.js';
import { signingBlockDefinition } from '../common/signing.js';
import { dateVal, numVal, optionChosen, optionVal, pickCode, selectSlot, shown, slotText, textVal, ticked, toSlotOptions, } from '../common/slots.js';
import { rows, str } from '../lib.js';
const SOURCE = 'base-templates/MALL_Blockhyra_företagsbostadsuthyrning.docx';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: SOURCE },
};
const SHARED = { plane: 'shared', status: 'published', language: 'sv-SE' };
/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
/** Legacy `parseNumberValue`: a number, or a numeric string with spaces / a decimal comma. */
function numberOrNull(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value.replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
/** The label/value grid; empty when every row dropped out. */
const grid = (items) => nodes(fieldRows(items));
/**
 * Output of a block that may cite a bilaga. It declares the host's whole
 * set, so the assembler numbers them in the contract's order wherever one
 * is first cited — "Bilaga N" here matches the Bilagor list.
 */
const withAnnexes = (ctx, out, diagnostics = []) => ({
    nodes: out,
    attachments: contextAttachments(ctx),
    diagnostics,
});
const annex = (ctx, input, key) => annexOrRule(ctx, input[key], key, 'medium');
/* ------------------------------------------------------------------ */
/* Option sets — codes are frozen (ADR-0062) and equal the stored value */
/* ------------------------------------------------------------------ */
export const PURPOSE_GROUND_3A_BODY = 'Hyresgästen upplåter lägenhetsenheterna till sina egna anställda eller en annan tydligt avgränsad krets av personer.';
export const PURPOSE_GROUND_3B_BODY = 'Blockhyresgästen (som är mellanhand) upplåter lägenhetsenheterna till en juridisk person som i sin tur erbjuder bostad åt sin personal (anställda, uppdragstagare, konsulter).';
/** Editor-only guidance (legacy `type: 'note'` fields obs_3a / obs_3b) — never printed. */
export const OBS_3A = 'Obs! Kretsen måste ha en koppling till hyresgästens verksamhet eller vara begränsad på annat definierat sätt. Upplåtelse till obestämd allmänhet uppfyller inte kravet.';
export const OBS_3B = 'Obs! Blockhyresgästen får inte upplåta direkt till slutanvändarnas anställda eller uppdragstagare. Upplåtelsen i andrahandsledet måste ske till den juridiska personen, som ska erbjuda bostad åt sin personal.';
export const PURPOSE_GROUND_OPTIONS = [
    {
        code: 'punkt_3a',
        label: `Punkt 3.a – ${PURPOSE_GROUND_3A_BODY}`,
        summary: 'Ground 3a: tenant sublets to its own employees or a clearly defined circle of persons.',
    },
    {
        code: 'punkt_3b',
        label: `Punkt 3.b – ${PURPOSE_GROUND_3B_BODY}`,
        summary: 'Ground 3b: block tenant (intermediary) sublets to a legal person that in turn houses its personnel.',
    },
];
export const CIRCLE_TYPE_OPTIONS = [
    { code: 'egna_anstallda', label: 'Egna anställda', summary: 'The circle consists of the tenant’s own employees.' },
    { code: 'annan_krets', label: 'Annan krets, specificerad nedan', summary: 'The circle is another defined group, specified in free text.' },
];
export const BLOCK_SCOPE_OPTIONS = [
    { code: 'inline_table', label: 'Ange enheterna i tabellen nedan', summary: 'The included units are listed inline in the contract table.' },
    { code: 'per_annex', label: 'Framgår av bilaga', summary: 'The included units are described in a separate annex.' },
];
export const CONDITION_OPTIONS = [
    { code: 'befintligt', label: 'Befintligt skick', summary: 'The block apartment is let in its existing condition.' },
    { code: 'avtalat_enligt_bilaga', label: 'Avtalat skick enligt bilaga', summary: 'The condition is agreed and specified in an annex.' },
];
export const RENT_TYPE_OPTIONS = [
    { code: 'total', label: 'Total hyra', summary: 'The stated yearly amount is the total rent.' },
    { code: 'exkl_tillagg', label: 'Hyra exkl. nedan markerade tillägg', summary: 'The stated rent excludes the supplements marked below.' },
];
export const MEDIA = [
    {
        key: 'heating',
        label: 'Kostnad för värme',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Heating cost is included in the rent.' },
            { code: 'tillagg_bilaga', label: 'Värmetillägg utgår enligt bifogad klausul', summary: 'A heating supplement applies per an attached clause/annex.' },
        ],
        annex: { key: 'heating_annex', label: 'Bilaga – värmetillägg' },
    },
    {
        key: 'cold_water',
        label: 'Kostnad för kallvatten',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Cold water cost is included in the rent.' },
            { code: 'tillagg_bilaga', label: 'Tillägg utgår enligt bifogad klausul', summary: 'A cold-water supplement applies per an attached clause/annex.' },
        ],
        annex: { key: 'cold_water_annex', label: 'Bilaga – kallvattentillägg' },
    },
    {
        key: 'hot_water',
        label: 'Kostnad för varmvatten',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Hot water cost is included in the rent.' },
            { code: 'tillagg_bilaga', label: 'Tillägg utgår enligt bifogad klausul', summary: 'A hot-water supplement applies per an attached clause/annex.' },
        ],
        annex: { key: 'hot_water_annex', label: 'Bilaga – varmvattentillägg' },
    },
    {
        key: 'va',
        label: 'VA-kostnad',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Water & sewage (VA) cost is included in the rent.' },
            { code: 'tillagg_bilaga', label: 'VA-tillägg utgår enligt bifogad klausul', summary: 'A VA supplement applies per an attached clause/annex.' },
        ],
        annex: { key: 'va_annex', label: 'Bilaga – VA-tillägg' },
    },
    {
        key: 'electricity',
        label: 'El till lägenheterna',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Electricity for the dwellings is included in the rent.' },
            { code: 'eget_abonnemang', label: 'Hyresgästen har eget abonnemang', summary: 'The block tenant holds its own electricity subscription.' },
            { code: 'andrahands_egna', label: 'Andrahandshyresgästerna har egna abonnemang', summary: 'The sub-tenants hold their own electricity subscriptions.' },
        ],
    },
    {
        key: 'stair_cleaning',
        label: 'Trappstädning',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Stair cleaning is included in the rent.' },
            { code: 'hyresgast', label: 'Ombesörjs och bekostas av hyresgästen', summary: 'Stair cleaning is arranged and paid for by the tenant.' },
        ],
    },
    {
        key: 'waste_collection',
        label: 'Sophämtning',
        options: [
            { code: 'ingar', label: 'Ingår i hyran', summary: 'Waste collection is included in the rent.' },
            { code: 'hyresgast', label: 'Ombesörjs och bekostas av hyresgästen', summary: 'Waste collection is arranged and paid for by the tenant.' },
        ],
    },
];
export const PAYMENT_PERIOD_OPTIONS = [
    { code: 'kalendermanad', label: 'Kalendermånad', summary: 'Rent paid in advance before the start of each calendar month.' },
    { code: 'kalenderkvartal', label: 'Kalenderkvartal', summary: 'Rent paid in advance before the start of each calendar quarter.' },
];
export const PAYMENT_METHOD_OPTIONS = [
    { code: 'avisering', label: 'Efter avisering (rekommenderas)', summary: 'Paid after invoice/notice issued by the landlord (recommended).' },
    { code: 'plusgiro', label: 'Genom insättning på PlusGiro', summary: 'Paid to a plusgiro account number.' },
    { code: 'bankgiro', label: 'Genom insättning på BankGiro', summary: 'Paid to a bankgiro account number.' },
];
export const SPARE_KEY_OPTIONS = [
    { code: 'medger', label: 'Medger', summary: 'Tenant permits the landlord to hold a spare key.' },
    { code: 'medger_inte', label: 'Medger inte', summary: 'Tenant does not permit the landlord to hold a spare key.' },
];
export const MAINTENANCE_TEXT = {
    hyresgast_inre: 'Hyresgästen ska utföra och bekosta erforderligt inre underhåll av blockhyreslägenheten. Åtagandet omfattar således såväl ytskikt som tillhörande inredning.',
    enligt_bilaga: 'Hyresgästens underhållsskyldighet framgår av särskild bilaga.',
};
export const MAINTENANCE_OPTIONS = [
    {
        code: 'hyresgast_inre',
        label: 'Hyresgästen ska utföra och bekosta erforderligt inre underhåll',
        summary: 'Tenant performs and pays for the required interior maintenance of the dwellings.',
    },
    { code: 'enligt_bilaga', label: 'Hyresgästens underhållsskyldighet framgår av särskild bilaga', summary: 'The tenant’s maintenance duty is set out in a separate annex.' },
];
/* ------------------------------------------------------------------ */
/* Avtalsuppgifter + parties                                           */
/* ------------------------------------------------------------------ */
export const avtalsuppgifter = {
    ...SHARED,
    blockKey: 'blockhyra.avtalsuppgifter',
    version: 'v1',
    title: 'Avtalsuppgifter',
    summary: 'The header strip above the preamble: the contract number and the date the agreement takes effect.',
    slots: [
        { key: 'contract_number', label: 'Kontraktsnummer', type: 'text', required: true, help: 'Egen referens på avtalet, t.ex. A-2042.' },
        { key: 'effective_date', label: 'Avtalets ikraftträdande', type: 'date', help: 'Det datum avtalet träder i kraft.' },
    ],
    compile: (input) => nodes(referenceLine([
        ['Kontraktsnummer', textVal(input, 'contract_number')],
        ['Avtalets ikraftträdande', textVal(input, 'effective_date')],
    ])),
};
export const hyresvard = partyDetailsBlock({
    blockKey: 'blockhyra.hyresvard',
    ref: 'hyresvard',
    title: 'Hyresvärd',
    role: 'lessor',
    ordinal: 0,
    summary: 'The landlord’s name, id number and contact details.',
    fields: [
        { key: 'landlord_name', label: 'Namn', field: 'name', required: true, help: 'Fullständigt namn eller företagsnamn' },
        { key: 'landlord_org_number', label: 'Personnr / Org.nr', field: 'legalId', required: true },
        { key: 'landlord_address', label: 'Adress', field: 'address' },
        { key: 'landlord_phone', label: 'Telefon', field: 'phone' },
        { key: 'landlord_email', label: 'E-post', field: 'email' },
    ],
});
export const blockhyresgast = partyDetailsBlock({
    blockKey: 'blockhyra.blockhyresgast',
    ref: 'blockhyresgast',
    title: 'Blockhyresgäst',
    role: 'lessee',
    ordinal: 0,
    summary: 'The block tenant’s (always an organisation) name, org number, notice address and contact details.',
    fields: [
        { key: 'tenant_name', label: 'Namn', field: 'name', required: true, help: 'Fullständigt namn eller företagsnamn' },
        { key: 'tenant_org_number', label: 'Organisationsnummer', field: 'legalId', required: true },
        { key: 'tenant_address', label: 'Aviseringsadress', field: 'address' },
        { key: 'tenant_phone', label: 'Telefon', field: 'phone' },
        { key: 'tenant_email', label: 'E-post', field: 'email' },
    ],
});
/* ------------------------------------------------------------------ */
/* §3–§8                                                               */
/* ------------------------------------------------------------------ */
export const upplatelseandamal = {
    ...LEGAL,
    blockKey: 'blockhyra.upplatelseandamal',
    version: 'v1',
    title: 'Upplåtelseändamål: företagsbostäder',
    summary: 'The ground for the company-housing block lease (3a own circle / 3b via a legal person), printed without its option prefix.',
    slots: [
        {
            key: 'purpose_ground',
            label: 'Tillämplig grund',
            type: 'select',
            required: true,
            help: 'Ange tillämplig grund (endast ett alternativ).',
            options: toSlotOptions(PURPOSE_GROUND_OPTIONS),
        },
        {
            key: 'obs_3a',
            label: 'Obs – avgränsad krets',
            type: 'note',
            help: OBS_3A,
            visibleWhen: { key: 'purpose_ground', equals: 'punkt_3a' },
        },
        {
            key: 'obs_3b',
            label: 'Obs – upplåtelse via juridisk person',
            type: 'note',
            help: OBS_3B,
            visibleWhen: { key: 'purpose_ground', equals: 'punkt_3b' },
        },
        selectSlot('circle_type', 'Kretsen utgörs av', CIRCLE_TYPE_OPTIONS, { visibleWhen: { key: 'purpose_ground', equals: 'punkt_3a' } }),
        {
            key: 'circle_specification',
            label: 'Avgränsningskriterium',
            type: 'textarea',
            help: 'T.ex. anställning hos, uppdrag för, tillhörighet till. Kretsen måste ha en koppling till hyresgästens verksamhet eller vara begränsad på annat definierat sätt.',
            visibleWhen: {
                all: [
                    { key: 'purpose_ground', equals: 'punkt_3a' },
                    { key: 'circle_type', equals: 'annan_krets' },
                ],
            },
        },
    ],
    compile: (input, ctx) => {
        const ground3b = optionChosen(PURPOSE_GROUND_OPTIONS, input.purpose_ground, 'punkt_3b');
        const is3a = shown(ctx, !ground3b);
        const is3b = shown(ctx, ground3b);
        const content = [];
        if (is3a) {
            content.push(p(PURPOSE_GROUND_3A_BODY), ...grid([
                ['Kretsen utgörs av', [optionVal(CIRCLE_TYPE_OPTIONS, input, 'circle_type')]],
                shown(ctx, optionChosen(CIRCLE_TYPE_OPTIONS, input.circle_type, 'annan_krets')) && [
                    'Avgränsningskriterium',
                    [textVal(input, 'circle_specification', 'long')],
                ],
            ]));
        }
        if (is3b)
            content.push(p(PURPOSE_GROUND_3B_BODY));
        return [section('upplatelseandamal', 'Upplåtelseändamål: företagsbostäder', content)];
    },
};
/** Legacy widths were 6/12/9/8 rem; carried as relative weights. */
export const BLOCK_UNIT_COLUMNS = [
    { key: 'objektsnummer', label: 'Objektsnummer', width: 17 },
    { key: 'adress', label: 'Adress', width: 34 },
    { key: 'utrymme', label: 'Ev. tillhörande utrymme', width: 26 },
    { key: 'undandras', label: 'Förhandlingsordningens tillämplighet', width: 23 },
];
const isYes = (value) => typeof value === 'string' && /^(ja|j|x|☑|true)$/i.test(value.trim());
export const omfattning = {
    ...LEGAL,
    blockKey: 'blockhyra.omfattning',
    version: 'v1',
    title: 'Blocklägenhetens omfattning',
    summary: 'The units in the block apartment (at least three), inline as a table or by reference to an annex.',
    slots: [
        selectSlot('block_scope_mode', 'Hur anges blocklägenhetens omfattning', BLOCK_SCOPE_OPTIONS, { required: true, default: 'inline_table' }),
        {
            key: 'block_units',
            label: 'Enheter i blocklägenheten (minst tre)',
            type: 'table',
            repeats: true,
            help: 'Lista de lägenheter som ingår i blocklägenheten. Minst tre enheter krävs.',
            visibleWhen: { key: 'block_scope_mode', equals: 'inline_table' },
            columns: BLOCK_UNIT_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'text' })),
        },
        {
            key: 'block_scope_annex',
            label: 'Bilaga – blocklägenhetens omfattning',
            type: 'attachment',
            required: true,
            help: 'Välj eller ladda upp bilagan som anger blocklägenhetens omfattning.',
            visibleWhen: { key: 'block_scope_mode', equals: 'per_annex' },
        },
    ],
    compile: (input, ctx) => {
        const diagnostics = [];
        const content = [];
        const units = rows(input.block_units);
        const perAnnex = optionChosen(BLOCK_SCOPE_OPTIONS, input.block_scope_mode, 'per_annex');
        if (shown(ctx, !perAnnex)) {
            const columns = BLOCK_UNIT_COLUMNS.map((c) => ({
                key: c.key,
                header: [t(c.label)],
                align: c.key === 'undandras' ? 'center' : 'left',
                width: c.width,
            }));
            const body = units.length === 0
                ? [{ cells: [{ content: [p([em('(Inga enheter ifyllda ännu)')], { align: 'center' })], colSpan: columns.length }] }]
                : units.map((row, index) => ({
                    cells: [
                        { content: [p([val(slotText(row.objektsnummer), { sourceKey: `block_units.${index}.objektsnummer`, blankWidth: 'short' })])] },
                        { content: [p([val(slotText(row.adress), { sourceKey: `block_units.${index}.adress`, blankWidth: 'short' })])] },
                        { content: [p([val(slotText(row.utrymme), { sourceKey: `block_units.${index}.utrymme`, blankWidth: 'short' })])] },
                        { content: [optionList([option(isYes(row.undandras), '', { code: isYes(row.undandras) ? 'ja' : 'nej', sourceKey: `block_units.${index}.undandras` })], { select: 'multi' })] },
                    ],
                }));
            content.push(p('I blocklägenheten ingår följande enheter:', { variant: 'fine_print' }), table(columns, body, { variant: 'data', repeatTableHeader: true }));
            if (ctx.mode !== 'blank' && optionChosen(BLOCK_SCOPE_OPTIONS, input.block_scope_mode, 'inline_table') && units.length < 3) {
                diagnostics.push({
                    severity: 'warning',
                    code: 'too_few_block_units',
                    message: 'En blocklägenhet ska omfatta minst tre enheter.',
                    slotKey: 'block_units',
                });
            }
        }
        if (shown(ctx, perAnnex)) {
            content.push(...grid([['Blocklägenhetens omfattning framgår av', annex(ctx, input, 'block_scope_annex')]]));
        }
        return withAnnexes(ctx, [section('omfattning', 'Blocklägenhetens omfattning', content)], diagnostics);
    },
};
export const skick = {
    ...LEGAL,
    blockKey: 'blockhyra.skick',
    version: 'v1',
    title: 'Blockhyreslägenhetens skick',
    summary: 'Whether the block apartment is let in its existing condition or an agreed condition set out in an annex.',
    slots: [
        selectSlot('condition', 'Blockhyreslägenhetens skick', CONDITION_OPTIONS, { required: true, default: 'befintligt' }),
        {
            key: 'condition_annex',
            label: 'Bilaga – avtalat skick',
            type: 'attachment',
            required: true,
            visibleWhen: { key: 'condition', equals: 'avtalat_enligt_bilaga' },
        },
    ],
    compile: (input, ctx) => {
        return withAnnexes(ctx, [
            section('skick', 'Blockhyreslägenhetens skick', grid([
                ['Skick', [optionVal(CONDITION_OPTIONS, input, 'condition')]],
                shown(ctx, optionChosen(CONDITION_OPTIONS, input.condition, 'avtalat_enligt_bilaga')) && [
                    'Bilaga – avtalat skick',
                    annex(ctx, input, 'condition_annex'),
                ],
            ])),
        ]);
    },
};
export const hyrestid = {
    ...LEGAL,
    blockKey: 'blockhyra.hyrestid',
    version: 'v1',
    title: 'Hyrestid',
    summary: 'The fixed lease term: from and to dates.',
    slots: [
        { key: 'lease_start_date', label: 'Från och med', type: 'date', required: true },
        { key: 'lease_end_date', label: 'Till och med', type: 'date', required: true },
    ],
    compile: (input, ctx) => {
        const start = str(input.lease_start_date);
        const end = str(input.lease_end_date);
        return {
            nodes: [
                section('hyrestid', 'Hyrestid', grid([
                    ['Från och med', [dateVal(ctx, input, 'lease_start_date')]],
                    ['Till och med', [dateVal(ctx, input, 'lease_end_date')]],
                ])),
            ],
            diagnostics: start && end && end < start
                ? [{ severity: 'warning', code: 'lease_period_inverted', message: 'Hyrestidens slutdatum ligger före startdatumet.', slotKey: 'lease_end_date' }]
                : [],
        };
    },
};
/** "X år och Y månader", or just one when the other is 0/empty. */
function extensionPhrase(input, ctx) {
    if (ctx.mode === 'blank') {
        return [val(null, { blankWidth: 'short', sourceKey: 'extension_years' }), t(' år och '), val(null, { blankWidth: 'short', sourceKey: 'extension_months' }), t(' månader')];
    }
    const years = numberOrNull(input.extension_years);
    const months = numberOrNull(input.extension_months);
    const parts = [];
    if (years && years > 0)
        parts.push(`${years} år`);
    if (months && months > 0)
        parts.push(`${months} månader`);
    return [val(parts.length > 0 ? parts.join(' och ') : null, { blankWidth: 'short', sourceKey: 'extension_years' })];
}
export const uppsagning = {
    ...LEGAL,
    blockKey: 'blockhyra.uppsagning',
    version: 'v1',
    title: 'Uppsägning',
    summary: 'Written notice period before the term ends, and the automatic extension period if no notice is given.',
    slots: [
        {
            key: 'notice_months',
            label: 'Uppsägningstid (månader)',
            type: 'number',
            required: true,
            help: 'Uppsägning ska ske skriftligen minst så här många månader före hyrestidens utgång.',
        },
        { key: 'extension_years', label: 'Förlängning (år)', type: 'number', help: 'Om avtalet inte sägs upp förlängs det med detta antal år för varje gång.' },
        { key: 'extension_months', label: 'Förlängning (månader)', type: 'number', help: 'Alternativt antal månader förlängningen avser för varje gång.' },
    ],
    compile: (input, ctx) => [
        section('uppsagning', 'Uppsägning', [
            p([
                t('Uppsägning av detta hyresavtal ska ske skriftligen minst '),
                numVal(ctx, input, 'notice_months'),
                t(' månader före den avtalade hyrestidens utgång. I annat fall är kontraktet förlängt med '),
                ...extensionPhrase(input, ctx),
                t(' för varje gång.'),
            ]),
        ], input.beside_previous === true ? { sideBySide: true } : {}),
    ],
};
export const hyra = {
    ...LEGAL,
    blockKey: 'blockhyra.hyra',
    version: 'v1',
    title: 'Hyra',
    summary: 'The yearly rent, whether it is total or excludes supplements, and how each media/service cost is handled.',
    slots: [
        { key: 'rent_yearly', label: 'Årshyra (kr/år)', type: 'currency', required: true },
        selectSlot('rent_type', 'Hyran utgör', RENT_TYPE_OPTIONS, { required: true, default: 'total' }),
        ...MEDIA.flatMap((m) => [
            selectSlot(m.key, m.label, m.options, { default: 'ingar' }),
            ...(m.annex
                ? [
                    {
                        key: m.annex.key,
                        label: m.annex.label,
                        type: 'attachment',
                        visibleWhen: { key: m.key, equals: 'tillagg_bilaga' },
                    },
                ]
                : []),
        ]),
    ],
    compile: (input, ctx) => {
        const mediaRows = MEDIA.map((m) => {
            const value = [optionVal(m.options, input, m.key)];
            if (m.annex && shown(ctx, optionChosen(m.options, input[m.key], 'tillagg_bilaga'))) {
                value.push(t(' (Bilaga: '), ...annex(ctx, input, m.annex.key), t(')'));
            }
            return [m.label, value];
        });
        return withAnnexes(ctx, [
            section('hyra', 'Hyra', [
                ...grid([
                    ['Kronor per år', [{ ...numVal(ctx, input, 'rent_yearly'), format: 'currency' }]],
                    ['Hyran utgör', [optionVal(RENT_TYPE_OPTIONS, input, 'rent_type')]],
                ]),
                p('Mediatyp / betalning', { variant: 'caption', keepWithNext: true }),
                ...grid(mediaRows),
            ]),
        ]);
    },
};
/* ------------------------------------------------------------------ */
/* Single-checkbox provisions: self-hide when off, plain text when on  */
/* ------------------------------------------------------------------ */
function toggleClause(spec) {
    return {
        ...LEGAL,
        blockKey: spec.blockKey,
        version: 'v1',
        title: spec.title,
        summary: spec.summary,
        slots: [spec.toggle, ...(spec.slots ?? [])],
        compile: (input, ctx) => {
            if (ctx.mode !== 'blank' && input[spec.toggle.key] !== true)
                return null;
            return withAnnexes(ctx, [section(spec.ref, spec.title, [p(spec.body(input, ctx))])]);
        },
    };
}
export const indexklausul = toggleClause({
    blockKey: 'blockhyra.indexklausul',
    ref: 'indexklausul',
    title: 'Indexklausul',
    summary: 'Rent is adjusted per an attached index clause. Omitted (and not numbered) unless chosen.',
    toggle: {
        key: 'index_clause_included',
        label: 'Indexreglering enligt bifogad indexklausul',
        type: 'boolean',
        help: 'Ändring av ovan angiven hyra sker i enlighet med bifogad indexklausul.',
    },
    slots: [
        {
            key: 'index_annex',
            label: 'Bilaga – indexklausul',
            type: 'attachment',
            required: true,
            visibleWhen: { key: 'index_clause_included', equals: true },
        },
    ],
    body: (input, ctx) => [
        t('Ändring av ovan angiven hyra sker i enlighet med bifogad indexklausul. Bilaga: '),
        ...annex(ctx, input, 'index_annex'),
    ],
});
export const betalning = {
    ...LEGAL,
    blockKey: 'blockhyra.betalning',
    version: 'v1',
    title: 'Hyrans betalning',
    summary: 'Rent is paid in advance per calendar month or quarter, and by which payment method.',
    slots: [
        {
            key: 'payment_period',
            label: 'Hyran betalas i förskott för varje',
            type: 'select',
            required: true,
            default: 'kalendermanad',
            help: 'Hyran betalas utan anfordran i förskott senast sista vardagen före varje periods början.',
            options: toSlotOptions(PAYMENT_PERIOD_OPTIONS),
        },
        selectSlot('payment_method', 'Betalningssätt', PAYMENT_METHOD_OPTIONS, { required: true, default: 'avisering' }),
        {
            key: 'payment_account',
            label: 'PlusGiro-/BankGiro-nummer',
            type: 'text',
            visibleWhen: { key: 'payment_method', oneOf: ['plusgiro', 'bankgiro'] },
        },
    ],
    compile: (input, ctx) => {
        const toAccount = optionChosen(PAYMENT_METHOD_OPTIONS, input.payment_method, 'plusgiro') ||
            optionChosen(PAYMENT_METHOD_OPTIONS, input.payment_method, 'bankgiro');
        return [
            section('betalning', 'Hyrans betalning', [
                p(`Hyran betalas utan anfordran i förskott senast sista vardagen före varje ${optionChosen(PAYMENT_PERIOD_OPTIONS, input.payment_period, 'kalenderkvartal') ? 'kalenderkvartals' : 'kalendermånads'} början.`),
                ...grid([
                    ['Betalningssätt', [optionVal(PAYMENT_METHOD_OPTIONS, input, 'payment_method')]],
                    shown(ctx, toAccount) && ['PlusGiro-/BankGiro-nummer', [textVal(input, 'payment_account')]],
                ]),
            ]),
        ];
    },
};
function staticSection(spec) {
    return { ...LEGAL, blockKey: spec.blockKey, version: 'v1', title: spec.title, summary: spec.summary, slots: [], compile: () => [section(spec.ref, spec.title, spec.body)] };
}
export const ranta = staticSection({
    blockKey: 'blockhyra.ranta',
    ref: 'ranta',
    title: 'Ränta, betalningspåminnelse',
    summary: 'Late payment carries statutory interest and a reminder fee.',
    body: [
        p('Vid försenad hyresbetalning ska hyresgästen betala dels ränta enligt räntelagen, dels ersättning för skriftlig betalningspåminnelse enligt lagstiftningen om ersättning för inkassokostnader m.m.'),
    ],
});
export const reservnycklar = {
    ...LEGAL,
    blockKey: 'blockhyra.reservnycklar',
    version: 'v1',
    title: 'Reservnycklar',
    summary: 'Whether the tenant permits the landlord to hold a spare key.',
    slots: [selectSlot('spare_key_consent', 'Hyresvärdens rätt att inneha reservnyckel', SPARE_KEY_OPTIONS, { required: true })],
    compile: (input) => {
        const consent = pickCode(SPARE_KEY_OPTIONS, input.spare_key_consent);
        const word = consent === 'medger_inte' ? 'medger inte' : consent === 'medger' ? 'medger' : null;
        return [
            section('reservnycklar', 'Reservnycklar', [
                p([
                    t('Hyresgästen '),
                    val(word, { raw: consent, sourceKey: 'spare_key_consent', blankWidth: 'medium' }),
                    t(' hyresvärden rätt att inneha reservnyckel till lägenheten.'),
                ]),
            ]),
        ];
    },
};
export const underhall = {
    ...LEGAL,
    blockKey: 'blockhyra.underhall',
    version: 'v1',
    title: 'Underhåll m m',
    summary: 'Who carries interior maintenance: the tenant, or as set out in a separate annex.',
    slots: [
        selectSlot('maintenance_mode', 'Underhållsansvar', MAINTENANCE_OPTIONS, { required: true, default: 'hyresgast_inre' }),
        {
            key: 'maintenance_annex',
            label: 'Bilaga – underhållsskyldighet',
            type: 'attachment',
            required: true,
            visibleWhen: { key: 'maintenance_mode', equals: 'enligt_bilaga' },
        },
    ],
    compile: (input, ctx) => {
        const byAnnex = optionChosen(MAINTENANCE_OPTIONS, input.maintenance_mode, 'enligt_bilaga');
        const second = [t(MAINTENANCE_TEXT.enligt_bilaga)];
        if (shown(ctx, byAnnex))
            second.push(t(' Bilaga: '), ...annex(ctx, input, 'maintenance_annex'));
        return withAnnexes(ctx, [
            section('underhall', 'Underhåll m m', [
                optionList([
                    option(ticked(ctx, !byAnnex), MAINTENANCE_TEXT.hyresgast_inre, { code: 'hyresgast_inre' }),
                    option(ticked(ctx, byAnnex), second, { code: 'enligt_bilaga' }),
                ], { sourceKey: 'maintenance_mode', select: 'single' }),
            ]),
        ]);
    },
};
export const besittningsskydd = toggleClause({
    blockKey: 'blockhyra.besittningsskydd',
    ref: 'besittningsskydd',
    title: 'Besittningsskydd för blockhyresgästen',
    summary: 'The parties agree on indirect security of tenure under the commercial-lease rules.',
    toggle: {
        key: 'possession_protection_indirect',
        default: true,
        label: 'Parterna avtalar om indirekt besittningsskydd (12 kap. 57–60 §§ JB)',
        type: 'boolean',
        help: 'Indirekt besittningsskydd enligt lokalhyresreglerna gäller för förlängning av detta avtal.',
    },
    body: () => [
        t('Parterna avtalar om indirekt besittningsskydd enligt lokalhyresreglerna (12 kap. 57–60 §§ JB) gäller för förlängning av detta avtal.'),
    ],
});
export const forutsattning = toggleClause({
    blockKey: 'blockhyra.forutsattning',
    ref: 'forutsattning',
    title: 'Förutsättning för avtalets giltighet',
    summary: 'The agreement requires the rent tribunal’s approval (12 kap. 1 e § JB) to be valid.',
    toggle: {
        key: 'requires_tribunal_approval',
        label: 'Avtalet kräver hyresnämndens tillstånd (12 kap. 1 e § JB)',
        type: 'boolean',
        help: 'Markera om avtalet för sin giltighet kräver hyresnämndens tillstånd enligt 12 kap. 1 e § jordabalken.',
    },
    body: () => [t('Avtalet kräver för sin giltighet hyresnämndens tillstånd enligt 12 kap. 1 e § jordabalken.')],
});
export const ansokan = toggleClause({
    blockKey: 'blockhyra.ansokan',
    ref: 'ansokan',
    title: 'Ansökan om hyresnämndens tillstånd',
    summary: 'The block tenant applies to the rent tribunal; the landlord is a party to the case.',
    toggle: {
        key: 'tenant_applies_approval',
        label: 'Blockhyresgästen ansöker om tillstånd hos hyresnämnden',
        type: 'boolean',
        help: 'Hyresvärden är part i ärendet.',
    },
    body: () => [t('Blockhyresgästen ska ansöka om tillstånd hos hyresnämnden. Hyresvärden är part i ärendet.')],
});
export const sarskildAnpassning = toggleClause({
    blockKey: 'blockhyra.sarskild-anpassning',
    ref: 'sarskild-anpassning',
    title: 'Särskild anpassning',
    summary: 'The tenant performs and pays for adaptations required by regulations for the activity.',
    toggle: {
        key: 'special_adaptation',
        label: 'Hyresgästen utför och bekostar särskild anpassning',
        type: 'boolean',
        help: 'Åtgärder som krävs för att objekt i blockhyreslägenheten ska uppfylla föreskrifter för aktuell verksamhet.',
    },
    body: () => [
        t('Hyresgästen utför och bekostar de åtgärder som krävs för att objekt i blockhyreslägenheten vid tillträdet eller senare ska uppfylla de föreskrifter som gäller för aktuell verksamhet.'),
    ],
});
export const allmannaVillkor = staticSection({
    blockKey: 'blockhyra.allmanna-villkor',
    ref: 'allmanna-villkor',
    title: 'Allmänna villkor',
    summary: 'General terms: no rent reduction for ordinary maintenance, the tenant’s duty of care and undertakings, and force majeure.',
    body: [
        h(3, 'Nedsättning av hyran vid sedvanligt underhåll', { keepWithNext: true }),
        p('Hyresgästen har inte rätt att få nedsättning av hyran för tid, under vilken hyresvärden låter verkställa arbete för sedvanligt underhåll av lägenheterna eller fastigheten. Arbetet ska dock utföras utan onödig tidsutdräkt.'),
        p('Bestämmelserna i 12 kap. JB om att hyresgästen ska väl vårda lägenheten och vad som hör till dessa samt vid lägenhetens användning se till att göra allt som krävs för att bevara sundhet, ordning och gott skick inom fastigheten avser blockhyreslägenheten och samtliga objekt som ingår i den.'),
        p('Hyresgästen förbinder sig', { keepWithNext: true }),
        // Unbulleted lines, as the legacy `list-none` list prints them.
        p([
            'att inte utan särskilt tillstånd sätta upp anslag, skyltar, markiser, utomhusantenner eller annat liknande på fastigheten samt att vid reparation av fastigheten på egen bekostnad ta ner och i förekommande fall sätta upp dem igen; meddelanden kan dock sättas upp på anslagstavla som är avsedd för sådana.',
            'att på egen bekostnad låta forsla bort för sopnedkast eller soptunna olämpligt avfall om inte hyresvärden särskilt tillhandahåller container eller liknande',
            'att hålla till lägenheterna hörande balkonger eller altaner fria från snö och is',
            'att i fastighet där trappstädning inte ingår i hyran i tur med övriga hyresgäster ombesörja trappstädningen',
            'att hålla lägenheterna tillgängliga för sotning och rensning av kanaler',
            'att vid avflyttning lämna lägenheterna väl rengjorda samt att då till hyresvärden överlämna samtliga port- och dörrnycklar, även om de har anskaffats av hyresgästen.',
        ].flatMap((line, i) => [...(i > 0 ? [br()] : []), t(line)])),
        p('Hyresgästens åtagande enligt ovan innebär också en skyldighet att verka för att andrahandshyresgästerna i förekommande fall i sin tur agerar i enlighet med åtagandet.'),
        p('Hyresvärden fritar sig från skyldighet att fullgöra sin del av avtalet och från skyldighet att betala skadestånd om hans eller hennes åtaganden inte alls eller endast till onormalt hög kostnad kan fullgöras på grund av krig eller upplopp eller på grund av sådan arbetsinställelse, blockad, eldsvåda, explosion eller ingrepp av offentlig myndighet som hyresvärden inte råder över och inte heller har kunnat förutse.'),
    ],
});
export const inskrivning = toggleClause({
    blockKey: 'blockhyra.inskrivning',
    ref: 'inskrivning',
    title: 'Inskrivning',
    summary: 'The lease may not be registered without the landlord’s consent.',
    toggle: { key: 'no_inscription', label: 'Hyresavtalet får inte inskrivas utan medgivande', type: 'boolean', default: true },
    body: () => [t('Hyresavtalet får inte inskrivas utan särskilt medgivande från hyresvärden.')],
});
export const personuppgifter = toggleClause({
    blockKey: 'blockhyra.personuppgifter',
    ref: 'personuppgifter',
    title: 'Hantering av personuppgifter',
    summary: 'Points the tenant to the attached personal-data processing notice.',
    toggle: {
        key: 'pdpa_info_included',
        default: true,
        label: 'Bifoga information om personuppgiftsbehandling',
        type: 'boolean',
        help: 'Information till hyresgäst om behandling av personuppgifter bifogas som bilaga. Avmarkera för att utelämna avsnittet.',
    },
    slots: [
        {
            key: 'pdpa_info_annex',
            label: 'Bilaga – information om personuppgiftsbehandling',
            type: 'attachment',
            help: 'Välj eller skapa personuppgiftsbilagan.',
            visibleWhen: { key: 'pdpa_info_included', equals: true },
        },
    ],
    body: (input, ctx) => [
        t('Information till hyresgäst om behandling av personuppgifter, se Bilaga: '),
        ...annex(ctx, input, 'pdpa_info_annex'),
    ],
});
export const OTHER_ANNEX_COLUMNS = [
    { key: 'bilaga', label: 'Bilaga' },
    { key: 'avseende', label: 'Avseende' },
];
export const ovrigaBestammelser = {
    ...LEGAL,
    blockKey: 'blockhyra.ovriga-bestammelser',
    version: 'v1',
    title: 'Övriga bestämmelser',
    summary: 'The additional annexes that form part of the agreement, each with what it concerns.',
    slots: [
        {
            key: 'other_annexes',
            label: 'Tilläggsbilagor',
            type: 'table',
            repeats: true,
            help: 'Som tillägg till detta avtal gäller följande bilagor — ange bilagans nummer och vad den avser.',
            columns: OTHER_ANNEX_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'text' })),
        },
    ],
    compile: (input) => {
        const annexRows = rows(input.other_annexes);
        return [
            section('ovriga-bestammelser', 'Övriga bestämmelser', [
                p('Som tillägg till detta avtal gäller följande bilagor:'),
                annexRows.length === 0
                    ? p([em('(Inga tilläggsbilagor angivna)')])
                    : optionList(annexRows.map((row, index) => option(true, [
                        t('Bilaga '),
                        val(slotText(row.bilaga), { sourceKey: `other_annexes.${index}.bilaga`, blankWidth: 'short' }),
                        t(' avseende '),
                        val(slotText(row.avseende), { sourceKey: `other_annexes.${index}.avseende` }),
                    ])), { select: 'multi' }),
            ]),
        ];
    },
};
/* ------------------------------------------------------------------ */
/* Bilagor (numbered) + Underskrift (unnumbered)                      */
/* ------------------------------------------------------------------ */
/**
 * The contract's attachments, listed from the host's attachment list. Every
 * attachment is declared here with its host number so the list can never
 * drift from what is actually attached.
 */
export const bilagor = {
    ...SHARED,
    blockKey: 'blockhyra.bilagor',
    version: 'v1',
    title: 'Bilagor',
    summary: 'Numbered last § listing every attachment the contract carries, as "Bilaga N – name".',
    slots: [],
    compile: (_input, ctx) => {
        const decls = contextAttachments(ctx);
        return {
            nodes: [
                section('bilagor', 'Bilagor', [decls.length ? attachmentList() : p([em('Inga bilagor bifogade.')])], {
                    keepTogether: true,
                }),
            ],
            attachments: decls,
        };
    },
};
/**
 * The legacy SigningBlock with `statement`: the execution statement above
 * the "Underskrifter" heading, physical vs digital coupled to the host's
 * place/date toggle (`ctx.custom.signing`). Unnumbered, kept together.
 */
export const signing = signingBlockDefinition({
    blockKey: 'blockhyra.signing',
    summary: 'Unnumbered signature block: the execution statement (physical vs digital), the heading and one panel per signer.',
    defaults: { statement: true },
});
export const BLOCKHYRA_BLOCKS = [
    avtalsuppgifter,
    hyresvard,
    blockhyresgast,
    upplatelseandamal,
    omfattning,
    skick,
    hyrestid,
    uppsagning,
    hyra,
    indexklausul,
    betalning,
    ranta,
    reservnycklar,
    underhall,
    besittningsskydd,
    forutsattning,
    ansokan,
    sarskildAnpassning,
    allmannaVillkor,
    inskrivning,
    personuppgifter,
    ovrigaBestammelser,
    bilagor,
    signing,
];
//# sourceMappingURL=blocks.js.map